<template>
  <b-modal id="tasks-modal"
           :title="$t('tasks')"
           hide-footer
           @show="onShow"
           modal-class="right">
    <div v-if="loaded">
      <div class="add-task-form">
        <div class="row row-narrow align-items-center mb-3" v-if="!allDays">
          <div class="col">
            <CRMDateNavigator  ref="navigator"
                               :left="true"
                               class="w-100"
                               @daySelected="daySelected"/>
          </div>
          <div class="col col-auto">
            <a class="btn-themed btn-themed-outline btn-themed-default d-inline-block w-100 text-center"
               tabindex="#"
               @click="todayClicked">
              {{ $t('today') }}
            </a>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <CheckboxGroup :label="$t('my_tasks')"
                           fid="modal_my_tasks"
                           @update:modelValue="tasksClicked"
                           v-model="myTasks"/>
          </div>
          <div class="col">
            <CheckboxGroup :label="$t('all_days')"
                           fid="modal_all_days"
                           @update:modelValue="tasksClicked"
                           v-model="allDays"/>
          </div>
        </div>

        <hr/>
        <a @click="addTask"
           v-if="!adding"
           class="d-flex justify-content-start align-items-center pointer">
          <div>
            <PlusSvg class=""/>
            <span class="ml-2">{{ $t('add_task') }}</span>
          </div>
        </a>
        <div v-if="adding">
          <AutosizeTextArea v-model="form.title"
                            :label="$t('text')"/>
          <SelectGroup variant="white"
                       v-model="form.importance"
                       :label="$t('importance')"
                       :options="options"
                       :showlabel="true"/>
          <div class="row no-gutters">
            <div class="col-6">
              <button class="btn-themed btn-themed-outline btn-themed-squared btn cancel mr-2 px-0 w-100"
                      @click="addTask">
                {{ $t('cancel') }}
              </button>
            </div>
            <div class="col-6">
              <button class="btn-themed btn ml-2 px-0 w-100  btn-themed-squared"
                      :disabled="!valid"
                      @click="save">
                {{ $t('save') }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-4">
        <TaskTeaser v-for="(task, tind) in tasks"
                    :key="`task-${task.id}-${tind}`"
                    @loadTasks="loadTasks"
                    :task="task"/>
      </div>
      <div v-if="hasMore" class="text-center my-4">
        <b-button class="btn btn-themed btn-themed-squared btn-themed-outline"
                  :disabled="loadingMore"
                  variant="themed"
                  @click="loadMore">
          {{ $t('load_more') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script setup>
import PlusSvg from '@/assets/svg-vue/header/plus.svg'
import AutosizeTextArea from "@/components/form/AutosizeTextArea"
import SelectGroup from "@/components/form/SelectGroup"
import { EloquentService } from "@/services/api/eloquent.api.service"
import TaskTeaser from "@/components/parts/header/TaskTeaser"
import CRMDateNavigator from "@/components/parts/crm/CRMDateNavigator"
import moment from "moment/moment"
import CheckboxGroup from "@/components/form/CheckboxGroup"
import { appendFormdata } from "@/extensions/prototypes/prototypes"
import { computed, nextTick, ref, watch } from "vue"
import { useStore } from "vuex"

const store = useStore()

const navigator = ref(null)
const adding = ref(false)
const loaded = ref(false)
const myTasks = ref(false)
const allDays = ref(false)
const page = ref(1)
const hasMore = ref(false)
const loadingMore = ref(false)
const form = ref({
  title: '',
  importance: 'low'
})
const options = [
  { id: 'low', title: 'low' },
  { id: 'medium', title: 'medium' },
  { id: 'high', title: 'high' },
]
const date = ref(moment().format('YYYY-MM-DD'))

const tasks = computed(() => store.state.auth.tasks)
const counter = computed(() => store.state.datatable.dataTableCounter)

const valid = computed(() => form.value.importance && form.value.title)

const getTasks = (params) => store.dispatch('getTasks', params)

// a new task in the datatable form modal
const addTask = () => {
  store.commit('setViewModel', 'task')
  store.commit('setModule', 'crm')
  store.commit('setSelectedModel', {})
  store.commit('setShowModal', true)
}

// the unfinished tasks of the day (or of all days), overdue first; only mine when asked
const loadTasks = async () => {
  const filters = {}
  if(myTasks.value) {
    filters.my_tasks = {"id":"my_tasks","condition":1,"type":"equals"}
  }
  let condition = { date: date.value }
  if(allDays.value) {
    condition = {}
  }
  condition.overDueFirst = true
  condition.finished = 0
  const response = await getTasks({
    condition,
    filters: JSON.stringify(filters),
    loading: true,
    page: page.value
  })
  hasMore.value = response?.last_page > response?.current_page
  loaded.value = true
}

const onShow = () => {
  page.value = 1
  loadTasks()
}

const loadMore = async () => {
  loadingMore.value = true
  page.value++
  await loadTasks()
  loadingMore.value = false
}

const save = () => {
  if(!valid.value) { return }
  adding.value = false
  let formData = new FormData()
  formData = appendFormdata(formData, form.value)
  EloquentService.create('task', formData).then(() => {
    getTasks({
      condition: {
        date: date.value
      }
    })
    form.value = {
      title: '',
      importance: 'low'
    }
  })
}

const daySelected = (val) => {
  page.value = 1
  date.value = val
  loadTasks()
}

// the navigator selects the day (daySelected)
const todayClicked = () => {
  page.value = 1
  navigator.value.day = moment()
}

const tasksClicked = async () => {
  await nextTick()
  page.value = 1
  await loadTasks()
}

watch(counter, () => {
  loadTasks()
})
</script>

<style scoped lang="scss">
.add-task-form {
  padding: 14px 22px 0 22px;
  svg {
    margin-top:-1px;
  }
}
</style>
